import React, { useState } from 'react';
import { VCLockGlyph, VCCheckGlyph, VCConstellation } from './glyphs';
import { t } from '../i18n';

interface Stage01ConsentProps {
  clinicConfig: {
    brand_name: string;
    logo_url?: string;
    privacy_policy_url?: string;
  };
  onAdvance: () => void;
}

export const Stage01Consent = ({ clinicConfig, onAdvance }: Stage01ConsentProps) => {
  const [consentGiven, setConsentGiven] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (consentGiven) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        onAdvance();
      }, 500);
    }
  };

  return (
    <div className="vc-frame stage-01-consent">
      <style>{`
        /* Stage 01: Consent — stage-specific styles only.
           Shared chamber vocabulary → widget-components.css */
        .stage-01-consent .vc-spinner {
          display: inline-block;
          width: 18px;
          height: 18px;
          border: 2px solid color-mix(in oklch, var(--fg-on-layer-live) 35%, transparent);
          border-top-color: var(--fg-on-layer-live);
          border-radius: 50%;
          animation: vc-spin 0.8s linear infinite;
        }
        @keyframes vc-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .stage-01-consent .vc-spinner { animation-duration: 1.6s; }
        }
      `}</style>

      <div className="vc-wordmark" style={{ marginTop: 'var(--sp-5)' }}>
        {clinicConfig.logo_url ? (
          <img src={clinicConfig.logo_url} alt={clinicConfig.brand_name} />
        ) : (
          clinicConfig.brand_name
        )}
      </div>

      <div style={{ display: 'grid', placeItems: 'center', marginTop: 'var(--sp-5)', position: 'relative' }}>
        <VCConstellation stage={8} size={96} dim={!consentGiven} complete />
        <span className="vc-constcap">
          {consentGiven ? t('consent.constellation.armed') : t('consent.constellation.begin')}
        </span>
      </div>

      <h1 className="vc-title" role="heading" aria-level={1} style={{ marginTop: 'var(--sp-4)' }}>
        {t('consent.title.line1')}<br />{t('consent.title.line2Pre')}<em>{t('consent.title.line2Em')}</em>
      </h1>

      <p className="vc-sub">
        {t('consent.sub')}
      </p>

      <form
        onSubmit={handleSubmit}
        aria-label={t('consent.formAria')}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, marginTop: 'var(--sp-5)' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div className="vc-privacy">
            <span className="vc-glyph">
              <VCLockGlyph size={20} />
            </span>
            <span>{t('consent.privacy')}</span>
          </div>

          <div className="vc-consent-row" role="group" aria-labelledby="consent-label">
            <label className="vc-checkbox-wrap">
              <input
                type="checkbox"
                id="consent-checkbox"
                aria-required="true"
                aria-describedby="consent-desc"
                checked={consentGiven}
                onChange={() => setConsentGiven(!consentGiven)}
              />
              <span className="vc-checkbox">
                <VCCheckGlyph size={14} />
              </span>
            </label>
            <label htmlFor="consent-checkbox" id="consent-label" className="vc-consent-label">
              {t('consent.agree')}
              <a
                href={clinicConfig.privacy_policy_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('consent.privacyLink')}
              </a>
            </label>
            {!consentGiven && (
              <p id="consent-desc" className="visually-hidden">
                {t('consent.desc')}
              </p>
            )}
          </div>
        </div>

        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
            paddingTop: 'var(--sp-6)',
            paddingBottom: 'var(--sp-6)',
          }}
        >
          <button
            type="submit"
            className="vc-btn vc-btn--primary"
            disabled={!consentGiven || isLoading}
            aria-disabled={!consentGiven || isLoading}
            aria-describedby={!consentGiven ? 'cta-hint' : undefined}
          >
            {isLoading ? (
              <span className="vc-spinner" aria-hidden="true"></span>
            ) : (
              t('consent.cta')
            )}
          </button>
          {!consentGiven && (
            <p id="cta-hint" className="visually-hidden">
              {t('consent.ctaHint')}
            </p>
          )}

          <p className="vc-legal">
            {t('consent.legal')}
          </p>
        </div>
      </form>
    </div>
  );
};
